import { X } from 'lucide-react';
import { Dialog as Primitive } from 'radix-ui';
import type { ComponentProps, HTMLAttributes } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * shadcn/ui Dialog, vendored, in the design system's v2 Modal look (TS-1681): its surface,
 * 20px radius, title and description colours and the bordered close button, from the copied
 * modal.tokens.css. The CRM keeps its own content component rather than the design system's
 * `ModalContent` because that one has a single 500px width, and the CRM's editors are wider.
 */
export const Dialog = Primitive.Root;
export const DialogTrigger = Primitive.Trigger;
export const DialogClose = Primitive.Close;

export function DialogContent({
  className,
  children,
  hideClose = false,
  ...props
}: ComponentProps<typeof Primitive.Content> & { hideClose?: boolean }) {
  return (
    <Primitive.Portal>
      <Primitive.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/40 dark:bg-black/60" />
      <Primitive.Content
        className={cn(
          'ds-v2 animate-pop-in shadow-raised fixed top-1/2 left-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-(--v2-modal-radius) bg-(--v2-modal-bg) p-6 [&>*]:min-w-0',
          // Fields are white on the modal's grey surface, as the design system's modal is drawn.
          '[&_:is([data-slot=input],[data-slot=textarea],[role=combobox])]:bg-background',
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <Primitive.Close className="focus-visible:ring-ring/50 absolute top-5 right-5 flex size-9 items-center justify-center rounded-[8px] border border-(--v2-modal-close-border) transition-colors hover:bg-(--v2-modal-close-bg-hover) focus-visible:ring-[3px] focus-visible:outline-none">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </Primitive.Close>
        )}
      </Primitive.Content>
    </Primitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1 pr-12 text-left', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-row justify-end gap-3', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof Primitive.Title>) {
  return (
    <Primitive.Title
      className={cn('text-xl leading-[1.44] font-medium text-(--v2-modal-title-fg)', className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof Primitive.Description>) {
  return (
    <Primitive.Description
      className={cn('text-sm leading-[1.56] text-(--v2-modal-text-secondary)', className)}
      {...props}
    />
  );
}
